import type { DevicesDevice, DevicesDeviceState } from '@smarthome/api';
import { useDebounce } from '@smarthome/ui';
import { useEffect, useRef, useState } from 'react';

import { byte255ToPercent, getDeviceOn, percentToByte255 } from '../../utils';

import { Slider, Toggle } from '@/components/ui';

export interface FanControlsProps {
  device: DevicesDevice;
  onUpdateState: (state: DevicesDeviceState) => void;
  disabled?: boolean;
}

export function FanControls({ device, onUpdateState, disabled }: FanControlsProps) {
  const capabilities = device.capabilities?.fan;
  const fanState = device.state?.fan;
  const isOn = getDeviceOn(device);

  // Local state for speed slider (prevents API spam while dragging)
  const rawIntensity = fanState?.intensity ?? 0;
  const [localSpeed, setLocalSpeed] = useState(() => byte255ToPercent(rawIntensity));
  const debouncedSpeed = useDebounce(localSpeed, 300);

  // Track if we initiated the change to avoid sync loops
  const isUserChange = useRef(false);
  const lastSentSpeed = useRef(debouncedSpeed);

  // Sync local state with device state when it changes externally
  useEffect(() => {
    if (!isUserChange.current) {
      setLocalSpeed(byte255ToPercent(rawIntensity));
    }
  }, [rawIntensity]);

  // Send debounced speed updates
  useEffect(() => {
    if (isUserChange.current && debouncedSpeed !== lastSentSpeed.current) {
      lastSentSpeed.current = debouncedSpeed;
      onUpdateState({ fan: { intensity: percentToByte255(debouncedSpeed) } });
      isUserChange.current = false;
    }
  }, [debouncedSpeed, onUpdateState]);

  const handlePowerToggle = (checked: boolean) => {
    onUpdateState({ fan: { on: checked } });
  };

  const handleSpeedChange = (value: number) => {
    isUserChange.current = true;
    setLocalSpeed(value);
  };

  const handleSwivelToggle = (checked: boolean) => {
    onUpdateState({ fan: { swivel: checked } });
  };

  // Check if this is an on/off-only fan
  const isOnOffOnly = !capabilities?.speed && !capabilities?.swivel;

  return (
    <div className="space-y-1">
      {/* Power toggle row */}
      <div className="rounded-lg px-3 py-2.5 transition-colors hover:bg-gray-100 dark:hover:bg-white/[0.02]">
        <Toggle
          label="Power"
          layout="switch-right"
          checked={isOn ?? false}
          onChange={handlePowerToggle}
          disabled={disabled}
        />
      </div>

      {/* On/off-only hint */}
      {isOnOffOnly && (
        <p className="px-3 text-xs text-gray-500 dark:text-zinc-500">
          This fan only supports on/off.
        </p>
      )}

      {/* Speed slider (only if supported) */}
      {capabilities?.speed && (
        <>
          <div className="mx-3 h-px bg-gray-200 dark:bg-white/[0.04]" />
          <div className="px-3 py-2.5">
            <Slider
              label="Speed"
              min={0}
              max={100}
              value={localSpeed}
              onChange={handleSpeedChange}
              formatValue={(v) => `${v}%`}
              disabled={disabled}
            />
          </div>
        </>
      )}

      {/* Swivel/Oscillation toggle (only if supported) */}
      {capabilities?.swivel && (
        <>
          <div className="mx-3 h-px bg-gray-200 dark:bg-white/[0.04]" />
          <div className="rounded-lg px-3 py-2.5 transition-colors hover:bg-gray-100 dark:hover:bg-white/[0.02]">
            <Toggle
              label="Oscillation"
              description="Fan rotates back and forth"
              layout="switch-right"
              checked={fanState?.swivel ?? false}
              onChange={handleSwivelToggle}
              disabled={disabled}
            />
          </div>
        </>
      )}
    </div>
  );
}
